// Spec 065 — Single live-trade bubble. Renders the formatted USDC amount with
// a `+` (buy) or `-` (sell) prefix in the outcome's resolved color, and plays
// a single drift+fade animation: the bubble travels from Y_START to Y_END
// while its opacity interpolates 0.25 → 1 → 0. The animation IS the bubble's
// visible lifetime.
//
// Sequencing note (spec 065 — queued release): the bubble is dumb — it plays
// one animation on mount and never starts timers of its own. The overlay's
// release queue (`live-trades-overlay.tsx`) decides WHEN a bubble mounts, so
// consecutive bubbles never visually overlap. The exported `STREAM_DURATION_S`
// is the bubble's visible lifetime; the overlay reads it to compute the
// release cadence (one bubble must clear before the next mounts).
//
// See `frontend/specs/065-live-trades/supplementary/contracts/live-trade-bubble.contract.md`.

'use client';

import { c as _c } from "react/compiler-runtime";
import { m, type TargetAndTransition, type Transition } from 'motion/react';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import type { LiveTrade } from './types';

// ---------------------------------------------------------------------------
// Module-scope animation constants (hoisted per constitution §12).
// ---------------------------------------------------------------------------

/** Vertical travel distance in px. Bubble flows upward from start to end.
 *  The bar-box is up to ~144px tall (CONTAINER_HEIGHT_PX); 64px gives a
 *  generous mid-bar drift before the bubble fades to opacity 0. */
const Y_END = -64;

/**
 * Stream duration in seconds — slow enough to read the value comfortably.
 * Exported so the overlay's release queue can derive its stagger interval
 * from the bubble's actual visible lifetime (no magic numbers).
 */
export const STREAM_DURATION_S = 0.9;

// Variants typed against Motion's public types so Biome's `useExplicitType`
// is satisfied while staying compatible with `m.span`'s prop shape.

const STREAM_INITIAL: TargetAndTransition = {
  y: 0,
  opacity: 0.25
};
const STREAM_ANIMATE: TargetAndTransition = {
  y: Y_END,
  opacity: [0.25, 1, 0]
};
const STREAM_TRANSITION: Transition = {
  duration: STREAM_DURATION_S,
  ease: 'linear',
  // Opacity keyframes: 0.25 at start, peak 1 at midpoint, fade to 0 at end.
  opacity: {
    duration: STREAM_DURATION_S,
    ease: 'linear',
    times: [0, 0.5, 1]
  }
};

/** Reduced-motion: snap to opaque, no Y travel, no transition. */
const REDUCED_MOTION_ANIMATE: TargetAndTransition = {
  y: 0,
  opacity: 1
};
const REDUCED_MOTION_TRANSITION: Transition = {
  duration: 0
};

/** Exit when caller removes the trade from the array. The animation has
 *  already faded to 0; instant exit prevents any flash. */
const EXIT_VARIANT: TargetAndTransition = {
  opacity: 0,
  transition: {
    duration: 0
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface LiveTradeBubbleProps {
  /** The trade to render. */
  trade: LiveTrade;
  /**
   * Tailwind text-color class resolved by the parent via `useOutcomeColor`
   * (the `c.text` field). The parent passes this rather than the bubble
   * resolving its own color so a single resolution path per outcome holds
   * across the bar fill, the option button, and the live-trade bubbles.
   */
  colorClass: string;
  /**
   * Pre-resolved by the parent. The parent reads `useReducedMotion()` once
   * and passes the boolean down so each bubble does not subscribe
   * independently to the OS preference event.
   */
  reduceMotion: boolean;
  /**
   * Spec 067 — mirrors the overlay's `anchor`. Flips the bubble's
   * `right-0` to `left-0` so the bubble visually originates from the
   * matching edge of the parent positioning context. Default `'right'`
   * preserves today's behaviour for every existing caller.
   */
  anchor?: 'left' | 'right';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LiveTradeBubble(props) {
  const $ = _c(12);
  const {
    trade,
    colorClass,
    reduceMotion,
    anchor: t0
  } = props;
  const anchor = t0 === undefined ? "right" : t0;
  const {
    formatD6
  } = useFormattedCurrency();
  const sign = trade.side === "buy" ? "+" : "-";
  let t1;
  if ($[0] !== formatD6 || $[1] !== trade.amountD6) {
    const formatted = formatD6(trade.amountD6, "compact");
    t1 = formatted.startsWith("-") ? formatted.slice(1) : formatted;
    $[0] = formatD6;
    $[1] = trade.amountD6;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const stripped = t1;
  const display = `${sign}${stripped}`;
  const horizontalAnchorClass = anchor === "left" ? "left-0" : "right-0";
  const t2 = reduceMotion ? false : STREAM_INITIAL;
  const t3 = reduceMotion ? REDUCED_MOTION_ANIMATE : STREAM_ANIMATE;
  const t4 = reduceMotion ? REDUCED_MOTION_TRANSITION : STREAM_TRANSITION;
  let t5;
  if ($[3] !== colorClass || $[4] !== horizontalAnchorClass) {
    t5 = cn("absolute top-0 text-caption font-medium font-mono whitespace-nowrap", horizontalAnchorClass, colorClass);
    $[3] = colorClass;
    $[4] = horizontalAnchorClass;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== display || $[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <m.span initial={t2} animate={t3} exit={EXIT_VARIANT} transition={t4} className={t5}>{display}</m.span>;
    $[6] = display;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}